/**
 * Offers side by side, on one published method.
 *
 * The comparison is the hardest thing on the platform to do honestly, because
 * the offers are not structured alike: one carries a flat arrangement fee, one
 * carries a monthly charge, one is a murabaha with a fixed profit amount and no
 * rate at all. Ranking them on a rate would favour whichever structure hides
 * its cost best.
 *
 * So every offer is reduced to a total cost of finance by one method, the
 * version of that method is stated on the page, and the components that were
 * excluded are listed rather than dropped. A charge that only arises if
 * something goes wrong is excluded by design, because averaging a default
 * charge into a comparison tells a business that is not going to default that
 * its finance costs more than it does.
 *
 * Nothing here is computed in the browser. The breakdown arrives from the
 * pricing engine, and this screen punctuates it.
 */
import type { Dict, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { Badge, Empty, Note } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel } from '../../components/Panel'

export function CompareOffers({ data }: { data: Dict }) {
  const { n, amount, d, lbl } = useSession()
  const fr = data.fr as Dict
  const section = data.section as SectionMeta
  const offers = (data.offers || []) as Dict[]
  const breakdowns = (data.breakdowns || {}) as Record<string, Dict>
  const method = data.method_version as string

  if (offers.length === 0) {
    return (
      <>
        <PageHead section={section} title={`Compare · ${fr.request_ref as string}`} />
        <Empty title="No open offer to compare">
          An offer that has expired or been withdrawn is not shown here, because
          a comparison including one would recommend something nobody can accept.
        </Empty>
      </>
    )
  }

  const cheapest = offers.reduce((best, o) => {
    const a = Number(o.total_cost_of_finance)
    const b = Number(best?.total_cost_of_finance)
    if (!Number.isFinite(a)) return best
    return !best || !Number.isFinite(b) || a < b ? o : best
  }, null as Dict | null)

  return (
    <>
      <PageHead section={section}
                title={`Compare offers · ${fr.request_ref as string}`}
                subtitle={`${offers.length} open offers on method ${method}`} />

      <Note kind="info">
        <b>Compared on total cost of finance, method {method}.</b> Each offer is
        reduced to what it costs over its own tenor, so two offers structured
        differently are comparable. Charges that arise only on default or early
        settlement are excluded and listed separately under each offer.
      </Note>

      <Panel title="Side by side" explain="offer.compare" tight>
        <div className="tbl-wrap">
          <table className="dl">
            <thead>
              <tr>
                <th>Provider</th><th>Product</th><th>Structure</th>
                <th className="num">Amount</th><th className="num">Tenor</th>
                <th className="num">Total cost</th>
                <th className="num">Total repayable</th>
                <th className="num">Cost per unit</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => {
                const b = breakdowns[String(o.offer_id)]
                const best = cheapest && o.offer_id === cheapest.offer_id
                return (
                  <tr key={String(o.offer_id)}>
                    <td data-label="Provider">
                      {o.provider_name as string}
                      {best && <> <Badge tone="good">lowest cost</Badge></>}
                    </td>
                    <td data-label="Product">{(o.product_name as string) || '—'}</td>
                    <td data-label="Structure">
                      {o.sharia_structure
                        ? <Badge tone="muted">{lbl(o.sharia_structure)}</Badge>
                        : <Badge tone="muted">conventional</Badge>}
                    </td>
                    <td data-label="Amount" className="num">
                      {amount(o.amount_offered, o.currency_code as string)}
                    </td>
                    <td data-label="Tenor" className="num">{n(o.tenor_months)} mo</td>
                    <td data-label="Total cost" className="num">
                      <b>{amount(b?.total_cost ?? o.total_cost_of_finance,
                                 o.currency_code as string)}</b>
                    </td>
                    <td data-label="Total repayable" className="num">
                      {amount(b?.total_repayable, o.currency_code as string)}
                    </td>
                    <td data-label="Cost per unit" className="num">
                      {b?.cost_per_sar === undefined ? '—' : n(b.cost_per_sar, 4)}
                    </td>
                    <td data-label="Expires">{o.expires_at ? d(o.expires_at) : '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="xs muted">
          Lowest cost is not a recommendation. A slower provider that costs less
          may be the wrong choice for a request with a deadline, and a cost
          difference inside a few hundred units is inside the uncertainty of any
          fee that depends on how the facility is actually used.
        </p>
      </Panel>

      {offers.map((o) => {
        const b = breakdowns[String(o.offer_id)]
        if (!b) return null
        const included = (b.included || []) as Dict[]
        const excluded = (b.excluded || []) as Dict[]
        return (
          <Panel key={String(o.offer_id)} explain="offer.compare" tight
                 title={`${o.provider_name as string} · how the figure is built`}>
            <div className="tbl-wrap">
              <table className="dl">
                <thead>
                  <tr>
                    <th>Component</th><th>Basis</th><th>Charged</th>
                    <th className="num">Rate</th><th className="num">Amount</th>
                    <th className="num">Cost over the tenor</th>
                  </tr>
                </thead>
                <tbody>
                  {included.map((c, i) => (
                    <tr key={i}>
                      <td data-label="Component">{(c.name_en as string)
                        || lbl(c.component_code)}</td>
                      <td data-label="Basis"><Badge tone="muted">{lbl(c.basis)}</Badge></td>
                      <td data-label="Charged">{lbl(c.charged_when)}</td>
                      <td data-label="Rate" className="num">
                        {c.value_pct === null || c.value_pct === undefined
                          ? '—' : `${n(Number(c.value_pct) * 100, 2)}%`}
                      </td>
                      <td data-label="Amount" className="num">
                        {c.value_amount === null || c.value_amount === undefined
                          ? '—' : amount(c.value_amount, o.currency_code as string)}
                      </td>
                      <td data-label="Cost over the tenor" className="num">
                        {amount(c.cost, o.currency_code as string)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <th colSpan={5}>Total cost of finance</th>
                    <td className="num"><b>
                      {amount(b.total_cost, o.currency_code as string)}
                    </b></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {excluded.length > 0 && (
              <>
                <h4>Excluded from the comparison</h4>
                <div className="tbl-wrap">
                  <table className="dl">
                    <thead>
                      <tr><th>Component</th><th>Charged</th><th>Why excluded</th></tr>
                    </thead>
                    <tbody>
                      {excluded.map((c, i) => (
                        <tr key={i}>
                          <td data-label="Component">{(c.name_en as string)
                            || lbl(c.component_code)}</td>
                          <td data-label="Charged">{lbl(c.charged_when)}</td>
                          <td data-label="Why excluded">
                            <Badge tone="warn">{String(c.excluded_because)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="xs muted">
                  These charges are real and they are listed because they are
                  real. They are left out of the comparable total because whether
                  they are ever paid depends on what happens, not on which offer
                  is taken.
                </p>
              </>
            )}

            <div className="scope-note">
              <Badge tone="muted">method {String(b.method_version)}</Badge>
              <Badge tone="muted">day basis {String(b.day_basis)}</Badge>
              {b.apr_equivalent !== undefined && (
                <Badge tone="info">
                  rate equivalent {n(Number(b.apr_equivalent) * 100, 2)}%
                </Badge>
              )}
            </div>
            <p className="xs muted">
              The rate equivalent is given for familiarity and is not the basis
              of the comparison. It is derived from the total cost on the average
              outstanding balance over a reducing schedule, which is why it
              differs from a headline rate a provider may quote.
            </p>
          </Panel>
        )
      })}
    </>
  )
}
